import './Navbar.scss'

import koalition from '../../../api/koalition'
import Dropdown from '../../core/Dropdown/Dropdown'
import NewCharacter from '../../modals/NewCharacter/NewCharacter'
import Tooltip from '../../core/Tooltip/Tooltip'
import useMessagesListener from '../../../hooks/MessagesListenerHook'
import Backdrop from '../../core/Backdrop/Backdrop'
import { useEffect, useState } from 'react'
import { useData } from '../../../contexts/Data'
import { useUser } from '../../../contexts/User'
import { useReady } from '../../../contexts/Ready'
import { useModal } from '../../../contexts/Modal'
import { Link, useNavigate } from 'react-router'
import { Roles } from '../../../utilities/constants'
import { useLoading } from '../../../contexts/Loading'
import { useCharacters } from '../../../contexts/Characters'
import { urlBase64ToUint8Array } from '../../../utilities/helpers'
import { useToaster } from '../../../contexts/Toaster'
import { env } from '../../../utilities/helpers'

const Navbar = ({}) => {    
    const data = useData()
    const user = useUser()
    const modal = useModal()
    const loading = useLoading()
    const ready = useReady()
    const characters = useCharacters()
    const toaster = useToaster()
    const navigate = useNavigate()
    const [drawerMenuOpen, setDrawerMenuOpen] = useState()
    const [previousCharacterServerId, setPreviousCharacterServerId] = useState()

    useMessagesListener()

    const routeLinks = [
        { label: 'Characters', path: '/characters' },
        { label: 'Events', path: '/events' },
        { label: 'Fixed Mge', path: '/mge' },
    ]

    const characterDropdownItems = [
        ...(characters.$state.user?.map((character) => {
            return { name: character.name, onClick: character.name !== characters.$state.current.name && (() => switchCharacter(character)), content: (
                <span className={`navbar-character ${character.name === characters.$state.current.name ? 'current' : ''}`}>
                    <span className="chip base dark small mr-2">{character.acceptedAlliance ? character.alliance?.tag : character.server.name}</span>
                    <span>{character.name}</span>
                </span>
            ) }
        }) || []),
        { name: 'new', onClick: () => modal.open({ title: 'New Character', component: <NewCharacter /> }), content: (
            <div className="flex-nowrap flex-center">
                <div className="add mr-3">
                    <img src="/images/icons/plus_squared.svg" />
                </div>

                <span className="text-nowrap">Add character</span>
            </div>
        ) }
    ]

    const userItems = [
        { name: 'email', label: user.$state?.email, onClick: () => navigate('/account') },
        { name: 'character', label: 'Character', onClick: () => navigate(`/characters/${characters.$state.current?._id}`), hidden: !characters.$state.current?._id },
        { name: 'preferences', label: 'Preferences', onClick: () => navigate('/preferences') },
        { name: 'logout', label: 'Logout', onClick: user.logout }
    ].filter((item) => !item.hidden)

    const configurationItems = [
        { name: 'approvals', content: (
            <span className="flex-center-y">
                <span>Approvals</span>
                {!!(characters.$state.server?.pending?.length || characters.$state.alliance?.pending?.length) && <span className="notification-dot ml-3"></span>}
            </span>
        ), onClick: () => navigate('/approvals'), authorized: characters.authorized([Roles.developer, Roles.admin, Roles.leader]) },
        { name: 'roles', label: 'Roles', onClick: () => navigate('/roles'), authorized: characters.authorized([Roles.developer, Roles.admin]) },
        { name: 'users', label: 'Users', onClick: () => navigate('/users'), authorized: characters.authorized([Roles.developer]) },
        { name: 'legions', label: 'Legions', onClick: () => navigate('/legions'), authorized: characters.authorized([Roles.developer, Roles.admin, Roles.leader]) },
        { name: 'stellarSeasons', label: 'Stellar Seasons', onClick: () => navigate('/stellarSeasons'), authorized: characters.authorized([Roles.developer]) },
        { name: 'alliances', label: 'Alliances', onClick: () => navigate('/alliances'), authorized: characters.authorized([Roles.developer, Roles.admin]) },
    ].filter((item) => item.authorized)

    const onDrawerItemClick = (item) => {
        if (item.onClick) {
            item.onClick()
            setDrawerMenuOpen(false)
        }
    }

    const switchCharacter = (character) => {
        characters.select(character)
        localStorage.setItem('lastSelectedCharacter', character.name)
    }

    const checkSubscription = async (userId) => {
        const notificationsSupported = ('serviceWorker' in navigator) && ('PushManager' in window)
      
        if (notificationsSupported) {
            try {
                const registration = await navigator.serviceWorker.register('/serviceWorker.js')
        
                registration.onupdatefound = () => {
                    const newWorker = registration.installing
                    newWorker.onstatechange = () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            newWorker.postMessage({ type: 'newVersion' })
                        }
                    }
                }
        
                navigator.serviceWorker.addEventListener('controllerchange', window.location.reload)
        
                const previousPermission = Notification.permission
                const permission = await Notification.requestPermission()
        
                if (permission === 'granted') {
                    if (previousPermission !== 'granted') toaster.info('Visit user preferences page to configure which notifications you wish to receive')

                    const existing = await registration.pushManager.getSubscription()
            
                    if (!existing) {
                        const vapidPublicKey = env.VAPID_PUBLIC_KEY
                        const convertedKey = urlBase64ToUint8Array(vapidPublicKey)
            
                        const subscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: convertedKey,
                        })
            
                        await koalition.post('subscriptions', { ...subscription.toJSON(), user: userId })
                    }
                }
            } catch (error) {
                console.error(error)
            }
        } else {
            const firstTime = !localStorage.notificationsUnsupportedWarningSent
            if (firstTime) {
                toaster.warning(`Push notifications are not supported by your browser. If you are using iOS please open the app in Safari and install it to home screen through Safari's options`, { duration: 8000 })
                localStorage.setItem('notificationsUnsupportedWarningSent', 'yes')
            }
        }
    }      

    useEffect(() => {
        koalition.defaults.headers.common.Character = characters.$state.current?._id
        if (characters.$state.current) {
            const differentServer = previousCharacterServerId !== characters.$state.current.server._id

            if (differentServer) {
                koalition.get(`/servers/${characters.$state.current.server._id}/characters`).then((response) => {
                    characters.server(response.data)
                }).catch((error) => {
                    data.save('characters', null)
                    console.error(error)
                })
            }

            if (characters.$state.current.alliance) {
                koalition.get(`/alliances/${characters.$state.current.alliance._id}/characters`).then((response) => {
                    characters.alliance(response.data)
                }).catch((error) => {
                    data.save('characters', null)
                    console.error(error)
                })
            } else characters.alliance([])

            setPreviousCharacterServerId(characters.$state.current.server._id)
        }
    }, [characters.$state.current])

    useEffect(() => {
        loading.start('user')
        koalition.get('/users/current').then((response) => {
            checkSubscription(response.data._id)
            user.save(response.data)
            characters.user(response.data.characters)
            if (response.data.characters.length) {
                const defaultCharacter = response.data.characters.find((character) => character.default === true)
                const lastSelectedCharacter = response.data.characters.find((character) => character.name === localStorage.lastSelectedCharacter)
                const currentCharacter = defaultCharacter || lastSelectedCharacter || response.data.characters[0]
                if (currentCharacter) localStorage.setItem('lastSelectedCharacter', currentCharacter.name)
                characters.select(currentCharacter)
            } else {
                modal.open({ title: 'New Character', component: <NewCharacter /> })
            }
            ready.set('user')
        }).catch((error) => {
            console.error(error)
            user.logout()
        }).finally(() => {
            loading.end('user')
        })
        koalition.get('/events/types').then((response) => {
            data.save('eventTypes', response.data)
        }).catch((error) => {
            console.error(error)
        })
    }, [])

    return (
        <nav className="container-fluid navbar py-3 px-4 mb-5">
            <div className="row justify-content-between">
                <div className="col left">
                    <div className="row h-100">
                        <div className="col-auto">
                            <Link className="logo flex-center-y" to="/">
                                <img src="/images/logos/logo.svg" />                            
                            </Link>
                        </div>

                        <div className="col-auto pl-0">
                            <ul className="routes d-flex">
                                {routeLinks.map((link) => (
                                    <li key={link.path}><Link to={link.path} className="link button base medium mx-2">{link.label}</Link></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {user.$state && !loading.$state.user && (
                    <div className="col-auto right user">
                        <div className="row">
                            <div className="col-auto characters">
                                {ready.$state.user && (
                                    <Tooltip text="Characters" position="bottom" align="right">
                                        <Dropdown items={characterDropdownItems} variant="button base medium" align="right">
                                            {characters.$state.current ? (
                                                <span>
                                                    <span className="chip base dark small mr-2">{characters.$state.current.acceptedAlliance ? characters.$state.current.alliance?.tag : characters.$state.current.server.name}</span>
                                                    <b>{characters.$state.current.name}</b>
                                                </span>
                                            ) : (
                                                <span>No character added</span>
                                            )}
                                        </Dropdown>
                                    </Tooltip>
                                )}
                            </div>

                            <div className="col-auto user pl-0 d-none d-md-flex">
                                {characters.authorized([Roles.developer, Roles.admin, Roles.leader]) && (
                                    <Tooltip text="Configuration" position="bottom" align="right">
                                        <Dropdown items={configurationItems} variant="button primary medium icon" align="right">
                                            <img src="/images/icons/wheel.svg" />
                                            {!!((characters.$state.server?.pending?.length && characters.authorized([Roles.developer, Roles.admin])) ||
                                                (characters.$state.alliance?.pending?.length && characters.authorized([Roles.developer, Roles.admin, Roles.leader]))) && (
                                                <span className="notification-dot floating ml-3"></span>
                                            )}
                                        </Dropdown>
                                    </Tooltip>
                                )}

                                <Tooltip text="User" position="bottom" align="right">
                                    <Dropdown items={userItems} variant="button primary medium icon ml-3" align="right">
                                        <img src="/images/icons/person.svg" />
                                    </Dropdown>
                                </Tooltip>
                            </div>

                        </div>
                    </div>
                )}
            </div>

            <button type="button" className="primary drawer d-md-none" onClick={() => setDrawerMenuOpen(!drawerMenuOpen)}>
                <img src="/images/icons/features.svg" />
            </button>

            {drawerMenuOpen && <Backdrop onClick={() => setDrawerMenuOpen(false)} />}
            <aside className={`drawer-menu d-md-none ${drawerMenuOpen ? 'open' : 'closed'} p-3`}>
                <div className="drawer-items">
                    <ul>
                        {routeLinks.map((link, linkIndex) => (
                            <li key={link.path} className="w-100" onClick={() => setDrawerMenuOpen(false)}>
                                <Link to={link.path} className="item p-4">{link.label}</Link>
                            </li>
                        ))}
                    </ul>

                    
                    {characters.authorized([Roles.developer, Roles.admin, Roles.leader]) && (
                        <section>
                            <hr />
                            <h3 className="card semi-dark mt-3 p-4">Configuration</h3>
                            <ul>
                                {configurationItems.map((item) => (
                                    <li className={`item p-4 ${item.disabled ? 'disabled' : ''}`} onClick={() => !item.disabled && onDrawerItemClick(item)} key={item.name}>{item.label || item.content}</li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section>
                        <hr className="mb-3" />
                        <ul>
                            {userItems.map((item) => (
                                <li className={`item p-4 ${item.disabled ? 'disabled' : ''}`} onClick={() => !item.disabled && onDrawerItemClick(item)} key={item.name}>{item.label || item.content}</li>
                            ))}
                        </ul>
                    </section>
                </div>
            </aside>
        </nav>
    )
}

export default Navbar